import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
interface FormData {
  first_name: string;
  last_name: string;
  contact_email: string;
  contact_phone: string;
  preferred_contact_method: string;
  business_name: string;
  abn: string;
  industry_primary: string;
  industry_sub: string[];
  industry_other_description: string;
  business_website: string;
  time_zone: string;
  location_city: string;
  service_area: string;
  business_size: string;
  annual_revenue_estimate: string;
  ai_purpose: string[];
  ai_function_other_description: string;
  ai_voice_gender: string;
  ai_accent: string;
  ai_accent_other: string;
  ai_languages: string[];
  ai_other_language_specify: string;
  ai_tone: string;
  ai_behaviour_notes: string;
  ai_greeting_script: string;
  ai_fallback_instructions: string;
  integration_needs: string[];
  integration_other_description: string;
  data_privacy_consent: boolean;
}
interface FormErrors {
  [key: string]: string;
}
export function OnboardingForm() {
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    contact_email: '',
    contact_phone: '',
    preferred_contact_method: 'email',
    business_name: '',
    abn: '',
    industry_primary: '',
    industry_sub: [],
    industry_other_description: '',
    business_website: '',
    time_zone: '',
    location_city: '',
    service_area: '',
    business_size: '',
    annual_revenue_estimate: '',
    ai_purpose: [],
    ai_function_other_description: '',
    ai_voice_gender: 'no_preference',
    ai_accent: '',
    ai_accent_other: '',
    ai_languages: [],
    ai_other_language_specify: '',
    ai_tone: 'friendly',
    ai_behaviour_notes: '',
    ai_greeting_script: '',
    ai_fallback_instructions: '',
    integration_needs: [],
    integration_other_description: '',
    data_privacy_consent: false
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const industrySubCategories: {
    [key: string]: Array<{
      value: string;
      label: string;
    }>;
  } = {
    tradies: [{
      value: 'electrician',
      label: 'Electrician'
    }, {
      value: 'plumber',
      label: 'Plumber'
    }, {
      value: 'carpenter',
      label: 'Carpenter'
    }, {
      value: 'roofer',
      label: 'Roofer'
    }, {
      value: 'flooring',
      label: 'Flooring / Tiler'
    }, {
      value: 'painter',
      label: 'Painter'
    }, {
      value: 'hvac',
      label: 'HVAC / Aircon'
    }, {
      value: 'builder_renovation',
      label: 'Builder / Renovations'
    }],
    health: [{
      value: 'clinic',
      label: 'Clinic / GP'
    }, {
      value: 'dental',
      label: 'Dental'
    }, {
      value: 'physio',
      label: 'Physiotherapy'
    }, {
      value: 'mental_health',
      label: 'Mental health / Counselling'
    }, {
      value: 'hospital',
      label: 'Hospital'
    }],
    legal: [{
      value: 'lawyers',
      label: 'Lawyers / Solicitors'
    }, {
      value: 'conveyancing',
      label: 'Conveyancer'
    }],
    education: [{
      value: 'school',
      label: 'School'
    }, {
      value: 'tafe',
      label: 'TAFE / Vocational'
    }, {
      value: 'tutor',
      label: 'Tutor / Private lessons'
    }],
    retail: [{
      value: 'store_front',
      label: 'Store front / Bricks & Mortar'
    }, {
      value: 'online_store',
      label: 'Online store / eCommerce'
    }],
    finance: [{
      value: 'accountant',
      label: 'Accountant'
    }, {
      value: 'financial_advisor',
      label: 'Financial advisor'
    }],
    real_estate: [{
      value: 'agency',
      label: 'Real estate agency'
    }, {
      value: 'property_mgmt',
      label: 'Property management'
    }],
    hospitality: [{
      value: 'restaurant',
      label: 'Restaurant'
    }, {
      value: 'cafe',
      label: 'Cafe / Takeaway'
    }, {
      value: 'hotel',
      label: 'Hotel / Accommodation'
    }],
    it_tech: [{
      value: 'software',
      label: 'Software / SaaS'
    }, {
      value: 'it_services',
      label: 'IT Services / MSP'
    }]
  };
  const validatePage = (page: number): boolean => {
    const newErrors: FormErrors = {};
    if (page === 1) {
      if (!formData.first_name.trim()) {
        newErrors.first_name = 'First name is required';
      }
      if (!formData.last_name.trim()) {
        newErrors.last_name = 'Last name is required';
      }
      if (!formData.contact_email.trim()) {
        newErrors.contact_email = 'Email address is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
        newErrors.contact_email = 'Please enter a valid email address';
      }
    }
    if (page === 2) {
      if (!formData.business_name.trim()) {
        newErrors.business_name = 'Business name is required';
      }
      if (!formData.industry_primary) {
        newErrors.industry_primary = 'Please select your primary industry';
      }
      if (!formData.time_zone) {
        newErrors.time_zone = 'Please select your time zone';
      }
    }
    if (page === 3) {
      if (formData.ai_purpose.length === 0) {
        newErrors.ai_purpose = 'Please select at least one AI function';
      }
      if (formData.ai_languages.length === 0) {
        newErrors.ai_languages = 'Please select at least one language';
      }
      if (!formData.data_privacy_consent) {
        newErrors.data_privacy_consent = 'You must agree to the privacy policy';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleNext = () => {
    if (validatePage(currentPage)) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const handlePrevious = () => {
    setCurrentPage(currentPage - 1);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePage(3)) {
      return;
    }
    setIsSubmitting(true);
    try {
      const submissionData = {
        _subject: 'New AI Voice Agent Request — WillAdd',
        _template: 'table',
        _captcha: 'false',
        ...formData,
        industry_sub: formData.industry_sub.join(', '),
        ai_purpose: formData.ai_purpose.join(', '),
        ai_languages: formData.ai_languages.join(', '),
        integration_needs: formData.integration_needs.join(', ')
      };
      const response = await fetch('https://formsubmit.co/ajax/noamanelkhaswani@gmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      if (response.ok) {
        setIsSubmitted(true);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error submitting the form. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData({
      ...formData,
      [field]: value
    });
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };
  const toggleArrayValue = (field: keyof FormData, value: string) => {
    const currentArray = formData[field] as string[];
    const newArray = currentArray.includes(value) ? currentArray.filter(v => v !== value) : [...currentArray, value];
    handleInputChange(field, newArray);
  };
  const toggleLanguage = (value: string) => {
    const currentLanguages = formData.ai_languages;
    if (currentLanguages.includes(value)) {
      handleInputChange('ai_languages', currentLanguages.filter(lang => lang !== value));
    } else {
      if (currentLanguages.length >= 3) {
        alert('You can select up to 3 languages');
        return;
      }
      handleInputChange('ai_languages', [...currentLanguages, value]);
    }
  };
  const progressPercentage = currentPage / 3 * 100;
  if (isSubmitted) {
    return <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-sky-50 flex items-center justify-center px-4 py-12" data-id="element-283">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="w-full max-w-2xl bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center" data-id="element-284">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-285">
            <CheckIcon size={40} className="text-white" data-id="element-286" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4" data-id="element-287">
            Thank You!
          </h2>
          <p className="text-lg text-gray-600 mb-6" data-id="element-288">
            Your AI Voice Agent request has been submitted successfully. Our
            team will review your information and contact you within 24 hours.
          </p>
          <div className="bg-indigo-50 rounded-xl p-6 mb-8 text-left" data-id="element-289">
            <h3 className="font-bold text-black mb-3" data-id="element-290">What Happens Next?</h3>
            <ul className="space-y-2 text-gray-600" data-id="element-291">
              <li data-id="element-292">• You'll receive a confirmation email</li>
              <li data-id="element-293">• Our specialist will contact you to discuss requirements</li>
              <li data-id="element-294">• We'll begin customizing your AI voice agent</li>
              <li data-id="element-295">• Your agent will be ready within 3-5 business days</li>
            </ul>
          </div>
          <Link to="/" className="inline-block px-8 py-3 bg-black text-white rounded-full font-semibold hover:bg-gray-900 transition-all" data-id="element-296">
            Return to Home
          </Link>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen bg-white py-8 px-4" data-id="element-297">
      <div className="max-w-4xl mx-auto" data-id="element-298">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl shadow-xl overflow-hidden" data-id="element-299">
          <div className="bg-gradient-to-br from-indigo-50 to-sky-50 p-6 md:p-8 border-b" data-id="element-300">
            <Link to="/" className="inline-block mb-4" data-id="element-301">
              <h1 className="text-2xl font-bold text-black" data-id="element-302">
                WellAd<span className="text-indigo-600" data-id="element-303">.</span>
              </h1>
            </Link>
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-2" data-id="element-304">
              AI Voice Agent Onboarding
            </h2>
            <p className="text-gray-600 mb-4" data-id="element-305">
              Complete these 3 steps to set up your AI voice agent
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-306">
              Fields marked * are required
            </p>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-307">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${progressPercentage}%`
            }} transition={{
              duration: 0.3
            }} className="h-full bg-gradient-to-r from-indigo-500 to-sky-500" data-id="element-308" />
            </div>
          </div>
          <form onSubmit={handleSubmit} data-id="element-309">
            <div className="p-6 md:p-8" data-id="element-310">
              {currentPage === 1 && <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-311">
                  <h3 className="text-xl md:text-2xl font-bold text-black mb-2" data-id="element-312">
                    Step 1 — Basic Contact
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-313">
                    Who should we contact about this AI voice agent?
                  </p>
                  <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-314">
                    <div data-id="element-315">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-316">
                        First name <span className="text-red-500" data-id="element-317">*</span>
                      </label>
                      <input type="text" value={formData.first_name} onChange={e => handleInputChange('first_name', e.target.value)} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition ${errors.first_name ? 'border-red-500' : 'border-gray-200'}`} placeholder="e.g. Noah" data-id="element-318" />
                      {errors.first_name && <p className="text-red-500 text-sm mt-1" data-id="element-319">
                          {errors.first_name}
                        </p>}
                    </div>
                    <div data-id="element-320">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-321">
                        Last name <span className="text-red-500" data-id="element-322">*</span>
                      </label>
                      <input type="text" value={formData.last_name} onChange={e => handleInputChange('last_name', e.target.value)} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition ${errors.last_name ? 'border-red-500' : 'border-gray-200'}`} placeholder="e.g. Smith" data-id="element-323" />
                      {errors.last_name && <p className="text-red-500 text-sm mt-1" data-id="element-324">
                          {errors.last_name}
                        </p>}
                    </div>
                  </div>
                  <div className="mb-6" data-id="element-325">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-326">
                      Email address <span className="text-red-500" data-id="element-327">*</span>
                    </label>
                    <input type="email" value={formData.contact_email} onChange={e => handleInputChange('contact_email', e.target.value)} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition ${errors.contact_email ? 'border-red-500' : 'border-gray-200'}`} placeholder="you@yourbusiness.com" data-id="element-328" />
                    {errors.contact_email && <p className="text-red-500 text-sm mt-1" data-id="element-329">
                        {errors.contact_email}
                      </p>}
                  </div>
                  <div className="mb-6" data-id="element-330">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-331">
                      Phone number
                    </label>
                    <input type="tel" value={formData.contact_phone} onChange={e => handleInputChange('contact_phone', e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition" placeholder="+61 4XX XXX XXX" data-id="element-332" />
                  </div>
                  <div data-id="element-333">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-334">
                      Preferred contact method
                    </label>
                    <div className="space-y-2" data-id="element-335">
                      {[{
                    value: 'email',
                    label: 'Email'
                  }, {
                    value: 'phone',
                    label: 'Phone / SMS'
                  }, {
                    value: 'whatsapp',
                    label: 'WhatsApp'
                  }].map(option => <label key={option.value} className="flex items-center" data-id="element-336">
                          <input type="radio" name="preferred_contact_method" value={option.value} checked={formData.preferred_contact_method === option.value} onChange={e => handleInputChange('preferred_contact_method', e.target.value)} className="mr-3" data-id="element-337" />
                          <span className="text-gray-700" data-id="element-338">{option.label}</span>
                        </label>)}
                    </div>
                  </div>
                </motion.div>}
              {currentPage === 2 && <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-339">
                  <h3 className="text-xl md:text-2xl font-bold text-black mb-2" data-id="element-340">
                    Step 2 — Business Information
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-341">
                    Tell us about your business
                  </p>
                  <div className="mb-6" data-id="element-342">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-343">
                      Business name <span className="text-red-500" data-id="element-344">*</span>
                    </label>
                    <input type="text" value={formData.business_name} onChange={e => handleInputChange('business_name', e.target.value)} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition ${errors.business_name ? 'border-red-500' : 'border-gray-200'}`} placeholder="Your Business Name" data-id="element-345" />
                    {errors.business_name && <p className="text-red-500 text-sm mt-1" data-id="element-346">
                        {errors.business_name}
                      </p>}
                  </div>
                  <div className="mb-6" data-id="element-347">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-348">
                      Primary industry <span className="text-red-500" data-id="element-349">*</span>
                    </label>
                    <select value={formData.industry_primary} onChange={e => {
                  handleInputChange('industry_primary', e.target.value);
                  handleInputChange('industry_sub', []);
                }} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition ${errors.industry_primary ? 'border-red-500' : 'border-gray-200'}`} data-id="element-350">
                      <option value="" data-id="element-351">Select your industry</option>
                      <option value="tradies" data-id="element-352">Tradies / Trades</option>
                      <option value="health" data-id="element-353">Health & Medical</option>
                      <option value="legal" data-id="element-354">Legal & Compliance</option>
                      <option value="education" data-id="element-355">Education</option>
                      <option value="retail" data-id="element-356">Retail / eCommerce</option>
                      <option value="finance" data-id="element-357">Finance & Accounting</option>
                      <option value="real_estate" data-id="element-358">Real Estate</option>
                      <option value="hospitality" data-id="element-359">Hospitality</option>
                      <option value="it_tech" data-id="element-360">IT / Software / SaaS</option>
                      <option value="other" data-id="element-361">Other</option>
                    </select>
                    {errors.industry_primary && <p className="text-red-500 text-sm mt-1" data-id="element-362">
                        {errors.industry_primary}
                      </p>}
                  </div>
                  {formData.industry_primary && formData.industry_primary !== 'other' && industrySubCategories[formData.industry_primary] && <div className="mb-6 p-4 bg-indigo-50 rounded-lg border-2 border-indigo-200" data-id="element-363">
                        <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-364">
                          Select your profession / specialty
                        </label>
                        <div className="space-y-3" data-id="element-365">
                          {industrySubCategories[formData.industry_primary].map(option => <label key={option.value} className="flex items-center p-3 bg-white rounded-lg hover:bg-indigo-100 transition cursor-pointer border border-gray-200" data-id="element-366">
                                <input type="checkbox" checked={formData.industry_sub.includes(option.value)} onChange={() => toggleArrayValue('industry_sub', option.value)} className="mr-3" data-id="element-367" />
                                <span className="text-gray-700 font-medium" data-id="element-368">
                                  {option.label}
                                </span>
                              </label>)}
                        </div>
                      </div>}
                  <div className="mb-6" data-id="element-369">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-370">
                      Time zone <span className="text-red-500" data-id="element-371">*</span>
                    </label>
                    <select value={formData.time_zone} onChange={e => handleInputChange('time_zone', e.target.value)} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition ${errors.time_zone ? 'border-red-500' : 'border-gray-200'}`} data-id="element-372">
                      <option value="" data-id="element-373">Select time zone</option>
                      <option value="Australia/Brisbane" data-id="element-374">
                        Australia / Brisbane (AEST)
                      </option>
                      <option value="Australia/Sydney" data-id="element-375">
                        Australia / Sydney (AEST/AEDT)
                      </option>
                      <option value="UTC" data-id="element-376">UTC</option>
                    </select>
                    {errors.time_zone && <p className="text-red-500 text-sm mt-1" data-id="element-377">
                        {errors.time_zone}
                      </p>}
                  </div>
                </motion.div>}
              {currentPage === 3 && <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} data-id="element-378">
                  <h3 className="text-xl md:text-2xl font-bold text-black mb-2" data-id="element-379">
                    Step 3 — AI Voice Setup
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-380">
                    Configure your AI voice agent
                  </p>
                  <div className="mb-6" data-id="element-381">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-382">
                      AI functions <span className="text-red-500" data-id="element-383">*</span>
                    </label>
                    <div className="space-y-2" data-id="element-384">
                      {[{
                    value: 'lead_capture',
                    label: 'Lead capture & qualification'
                  }, {
                    value: 'bookings',
                    label: 'Appointment booking'
                  }, {
                    value: 'customer_support',
                    label: 'Customer support'
                  }].map(option => <label key={option.value} className="flex items-center" data-id="element-385">
                          <input type="checkbox" checked={formData.ai_purpose.includes(option.value)} onChange={() => toggleArrayValue('ai_purpose', option.value)} className="mr-3" data-id="element-386" />
                          <span className="text-gray-700" data-id="element-387">{option.label}</span>
                        </label>)}
                    </div>
                    {errors.ai_purpose && <p className="text-red-500 text-sm mt-1" data-id="element-388">
                        {errors.ai_purpose}
                      </p>}
                  </div>
                  <div className="mb-6" data-id="element-389">
                    <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-390">
                      Languages <span className="text-red-500" data-id="element-391">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-392">
                      {[{
                    value: 'en_AU',
                    label: 'English (AU)'
                  }, {
                    value: 'en_US',
                    label: 'English (US)'
                  }, {
                    value: 'es',
                    label: 'Spanish'
                  }].map(lang => <button key={lang.value} type="button" onClick={() => toggleLanguage(lang.value)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${formData.ai_languages.includes(lang.value) ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-393">
                          {lang.label}
                        </button>)}
                    </div>
                    {errors.ai_languages && <p className="text-red-500 text-sm mt-1" data-id="element-394">
                        {errors.ai_languages}
                      </p>}
                  </div>
                  <div className="mb-6" data-id="element-395">
                    <label className="flex items-start" data-id="element-396">
                      <input type="checkbox" checked={formData.data_privacy_consent} onChange={e => handleInputChange('data_privacy_consent', e.target.checked)} className="mr-3 mt-1" data-id="element-397" />
                      <span className="text-sm text-gray-700" data-id="element-398">
                        <span className="text-red-500" data-id="element-399">*</span> I agree to the
                        privacy policy and terms of service
                      </span>
                    </label>
                    {errors.data_privacy_consent && <p className="text-red-500 text-sm mt-1" data-id="element-400">
                        {errors.data_privacy_consent}
                      </p>}
                  </div>
                </motion.div>}
            </div>
            <div className="flex justify-between items-center p-6 md:p-8 border-t bg-gray-50" data-id="element-401">
              {currentPage > 1 ? <button type="button" onClick={handlePrevious} className="px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-50 transition-all" data-id="element-402">
                  Previous
                </button> : <Link to="/" className="px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-50 transition-all" data-id="element-403">
                  Cancel
                </Link>}
              {currentPage < 3 ? <button type="button" onClick={handleNext} className="px-6 py-3 bg-black text-white rounded-full font-semibold hover:bg-gray-900 transition-all" data-id="element-404">
                  Next
                </button> : <button type="submit" disabled={isSubmitting} className="px-6 py-3 bg-black text-white rounded-full font-semibold hover:bg-gray-900 transition-all disabled:opacity-50" data-id="element-405">
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>}
            </div>
          </form>
        </motion.div>
      </div>
    </div>;
}